import { cn } from "@win-reality/react-ui";

interface DataTileProps {
  title?: string;
  data?: number | string | undefined;
  units?: string;
  onClick?: () => void;
  rowContainer?: boolean;
  hidden?: boolean;
  expandToContainer?: boolean;
}

/**
 * DataTile - Displays a single metric value
 *
 * Shows:
 * - Title (metric name like "Velo", "Spin", "EV")
 * - Data value (the actual measurement)
 * - Units (MPH, RPM, degrees, etc.)
 *
 * Features:
 * - Clickable to open tile change modal
 * - Hover effects
 * - Responsive sizing for row vs column layouts
 * - Hidden state for tile management
 */
export const DataTile = ({
  title,
  data,
  units,
  onClick,
  rowContainer = false,
  hidden = false,
  expandToContainer = false,
}: DataTileProps) => {
  if (hidden) return null;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={onClick}
      className={cn(
        // Layout
        "flex flex-col items-center justify-center",
        // Border
        "rounded",
        // Background
        "bg-white",
        // Effects
        "shadow-md transition-colors",
        // States
        "cursor-pointer hover:bg-[hsla(218,100%,52%,0.05)]",
        // Size variants
        expandToContainer && "w-full h-[400px] p-10",
        !expandToContainer &&
          rowContainer &&
          "min-w-[100px] flex-1 h-[100px] p-2.5",
        !expandToContainer &&
          !rowContainer &&
          "w-full flex-1 min-h-[60px] p-2.5",
      )}
    >
      {/* Title */}
      <div
        className={cn(
          "flex items-center justify-center h-1/4 text-center",
          expandToContainer ? "text-xl" : "text-sm",
        )}
      >
        <span className="text-text-light font-normal">
          {title?.substring(0, 10)}
        </span>
      </div>

      {/* Metric Value */}
      <div
        className={cn(
          "flex items-center justify-center h-1/2",
          expandToContainer ? "text-8xl" : "text-xl",
        )}
      >
        <span className="text-charcoal-900">
          {!data || data === "NaN" ? "--" : data}
        </span>
      </div>

      {/* Units */}
      <div
        className={cn(
          "flex items-center justify-center h-1/4 uppercase",
          expandToContainer ? "text-lg" : "text-xs",
        )}
      >
        <span className="text-gray-500">{units ? units : "-"}</span>
      </div>
    </div>
  );
};
